/*
  <fitment-chart>: the "vehicles this wheel fits" list on the product page.

  The element sits inside a <details>. The vehicle database is requested the
  first time the details element is opened (or as soon as the chart scrolls
  into view when it starts open). Every vehicle row is then compared with the
  selected variant through Theme.Fitment.evaluate() and sorted into groups:

    bolt-on  should bolt on
    mods     fits with modifications
    pattern  the bolt pattern can match but the fit is not confirmed: the
             variant lists no size and offset, the database has no size data
             for that vehicle, or the vehicle was built with several bolt
             patterns and the wheel has only one of them

  The grouping is plain data work, kept apart from the element and exposed as
  Theme.FitmentChart so it can be checked outside the browser.
*/
(function () {
  const Theme = window.Theme;
  if (!Theme || !Theme.Fitment) return;

  const Fitment = Theme.Fitment;
  const GROUPS = ['bolt-on', 'mods', 'pattern'];

  function groupOf(result) {
    if (result.level === 'bolt-on' || result.level === 'mods') return result.level;
    if (result.level === 'unknown' && (result.reason === 'boltPatternOnly' || result.reason === 'patternVaries')) return 'pattern';
    return null;
  }

  /* Joins year ranges that overlap or follow one another: 2006-2011 + 2012-2015 = 2006-2015. */
  function mergeRanges(ranges) {
    const sorted = ranges.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const merged = [];
    sorted.forEach(([from, to]) => {
      const last = merged[merged.length - 1];
      if (last && from <= last[1] + 1) last[1] = Math.max(last[1], to);
      else merged.push([from, to]);
    });
    return merged;
  }

  /*
    Returns { 'bolt-on': [...], mods: [...], pattern: [...] } where each group is
    a list of { make, models: [{ model, ranges: [[from, to], ...] }] } sorted by
    make, then model. Returns null when the wheel has no bolt pattern to go on.
  */
  function build(rows, wheel) {
    if (!wheel || !wheel.pcds || !wheel.pcds.length) return null;

    const collected = {};
    GROUPS.forEach((group) => {
      collected[group] = new Map();
    });

    rows.forEach((row) => {
      const group = groupOf(Fitment.evaluate(row, wheel));
      if (!group) return;
      const makes = collected[group];
      if (!makes.has(row.make)) makes.set(row.make, new Map());
      const models = makes.get(row.make);
      if (!models.has(row.model)) models.set(row.model, []);
      models.get(row.model).push([row.from, row.to]);
    });

    const chart = {};
    GROUPS.forEach((group) => {
      chart[group] = Array.from(collected[group].keys())
        .sort((a, b) => a.localeCompare(b))
        .map((make) => {
          const models = collected[group].get(make);
          return {
            make,
            models: Array.from(models.keys())
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
              .map((model) => ({ model, ranges: mergeRanges(models.get(model)) })),
          };
        });
    });
    return chart;
  }

  /* Narrows a chart to the makes and models that contain every word of the query. */
  function filter(chart, query) {
    const words = String(query || '')
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) return chart;

    const matches = (text) => words.every((word) => text.includes(word));
    const filtered = {};
    GROUPS.forEach((group) => {
      filtered[group] = [];
      chart[group].forEach((entry) => {
        const make = entry.make.toLowerCase();
        const models = matches(make)
          ? entry.models
          : entry.models.filter((item) => matches(`${make} ${item.model.toLowerCase()}`));
        if (models.length) filtered[group].push({ make: entry.make, models });
      });
    });
    return filtered;
  }

  function count(entries) {
    return entries.reduce((total, entry) => total + entry.models.length, 0);
  }

  function formatRanges(ranges) {
    return ranges.map(([from, to]) => (from === to ? String(from) : `${from}–${to}`)).join(', ');
  }

  Theme.FitmentChart = { GROUPS, build, filter, count, mergeRanges, formatRanges };

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  if (customElements.get('fitment-chart')) return;

  class FitmentChart extends HTMLElement {
    connectedCallback() {
      if (!this.product) {
        const data = this.querySelector('script[type="application/json"]');
        try {
          this.product = data ? JSON.parse(data.textContent) : null;
        } catch (error) {
          this.product = null;
        }
      }
      if (!this.product || !Array.isArray(this.product.variants)) return;

      this.details = this.closest('details');
      this.saved = this.querySelector('[data-chart-saved]');
      this.filterField = this.querySelector('[data-chart-filter]');
      this.input = this.filterField ? this.filterField.querySelector('input') : null;
      this.message = this.querySelector('[data-chart-message]');
      this.output = this.querySelector('[data-chart-output]');
      if (!this.output || !this.message) return;

      if (!this.bound) {
        this.bound = true;
        this.variantId = Number(this.dataset.variantId) || null;
        if (this.input) this.input.addEventListener('input', () => this.render());
        if (this.details) {
          this.details.addEventListener('toggle', () => {
            if (this.details.open) this.start();
          });
        }
      }

      (this.stops || []).forEach((stop) => stop());
      this.stops = [
        Theme.on('vehicle:changed', () => this.render()),
        Theme.on('variant:changed', (event) => {
          const detail = event.detail || {};
          if (detail.sectionId && detail.sectionId !== this.dataset.sectionId) return;
          this.variant = detail.variant || null;
          this.variantId = detail.variant ? Number(detail.variant.id) : null;
          this.update();
        }),
      ];

      if (!this.details || this.details.open) this.startWhenVisible();
    }

    disconnectedCallback() {
      (this.stops || []).forEach((stop) => stop());
      this.stops = [];
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    startWhenVisible() {
      if (this.started || this.observer) return;
      if (!('IntersectionObserver' in window)) {
        this.start();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.observer = null;
          this.start();
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    }

    async start() {
      if (this.started) return;
      this.started = true;
      this.showMessage(this.dataset.textLoading);
      try {
        this.rows = await Fitment.load();
      } catch (error) {
        /* opening the details element again retries the request */
        this.started = false;
        this.showMessage((Theme.strings.fitment || {}).loadError);
        return;
      }
      this.update();
    }

    /* Text is only written when it changes, so the status is announced once and not on every keystroke. */
    showMessage(text) {
      if (this.message.textContent !== (text || '')) this.message.textContent = text || '';
      this.message.hidden = !text;
    }

    currentVariant() {
      if (this.variantId === null) return null;
      const listed = this.product.variants.find((item) => Number(item.id) === this.variantId);
      if (listed) return listed;
      /* products with more variants than the embedded list: the one from the event, or the one rendered as selected */
      const matches = (item) => item && Number(item.id) === this.variantId && Array.isArray(item.options);
      return [this.variant, this.product.selected].find(matches) || null;
    }

    /* Re-evaluates every vehicle for the selected variant. */
    update() {
      if (!this.rows) return;
      const variant = this.currentVariant();
      this.wheel = variant ? Fitment.wheelFromVariant(this.product, variant) : null;
      this.chart = build(this.rows, this.wheel);
      this.render();
    }

    render() {
      if (!this.rows) return;

      if (!this.chart) {
        this.output.replaceChildren();
        this.output.hidden = true;
        if (this.filterField) this.filterField.hidden = true;
        if (this.saved) this.saved.hidden = true;
        this.showMessage((Theme.strings.fitment || {}).noSpecs);
        return;
      }

      this.renderSaved();

      const total = GROUPS.reduce((sum, group) => sum + count(this.chart[group]), 0);
      if (this.filterField) this.filterField.hidden = total === 0;
      if (!total) {
        this.output.replaceChildren();
        this.output.hidden = true;
        this.showMessage(this.dataset.textNone);
        return;
      }

      const visible = filter(this.chart, this.input ? this.input.value : '');
      const fragment = document.createDocumentFragment();
      GROUPS.forEach((group) => {
        if (visible[group].length) fragment.append(this.renderGroup(group, visible[group]));
      });

      const shown = fragment.childNodes.length > 0;
      this.output.replaceChildren(fragment);
      this.output.hidden = !shown;
      this.showMessage(shown ? '' : this.dataset.textNoMatches);
    }

    groupLabel(group) {
      if (group === 'pattern') return this.dataset.textPatternOnly;
      return Fitment.verdictLabel(group);
    }

    renderGroup(group, entries) {
      const section = element('section', `fitment-chart__group fitment-chart__group--${group}`);
      const heading = element('h3', 'fitment-chart__heading', this.groupLabel(group));
      heading.append(' ', element('span', 'fitment-chart__count', `(${count(entries)})`));

      const list = element('dl', 'fitment-chart__makes');
      entries.forEach((entry) => {
        const row = element('div', 'fitment-chart__make');
        const models = element('ul', 'fitment-chart__models');
        models.setAttribute('role', 'list');
        entry.models.forEach((item) => {
          const model = element('li', 'fitment-chart__model', `${item.model} `);
          model.append(element('span', 'fitment-chart__years', formatRanges(item.ranges)));
          models.append(model);
        });
        const cell = element('dd', 'fitment-chart__cell');
        cell.append(models);
        row.append(element('dt', 'fitment-chart__name', entry.make), cell);
        list.append(row);
      });

      section.append(heading, list);
      return section;
    }

    /* Says whether the shopper's saved vehicle is in the list. */
    renderSaved() {
      if (!this.saved) return;
      const vehicle = Fitment.getVehicle();
      if (!vehicle) {
        this.saved.hidden = true;
        this.saved.textContent = '';
        return;
      }
      const result = Fitment.evaluate(Fitment.findRow(this.rows, vehicle), this.wheel);
      const group = groupOf(result);
      const name = Fitment.label(vehicle);
      let text;
      if (group) {
        text = (this.dataset.textSavedListed || '').replace('[vehicle]', name).replace('[group]', this.groupLabel(group));
      } else {
        const detail = Fitment.verdictDetail(result);
        text = (this.dataset.textSavedNotListed || '').replace('[vehicle]', name);
        if (detail) text = `${text} ${detail}`;
      }
      if (this.saved.textContent !== text) this.saved.textContent = text;
      this.saved.className = `fitment-chart__saved fitment-chart__saved--${group || 'none'}`;
      this.saved.hidden = !text;
    }
  }

  customElements.define('fitment-chart', FitmentChart);
})();
